// Dashboard Page
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { StatusBadge, ScoreDisplay, ScoreBar } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { statsApi, evaluationsApi, awardsApi, exportApi, eventsApi } from '../services/api';
import { 
  TrendingUp, 
  Calendar, 
  ClipboardCheck, 
  Trophy,
  Plus,
  ArrowRight,
  Star,
  FileText,
  Download,
  Briefcase,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  FileSpreadsheet
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';

// Compute a single "underutilized score" for ranking.
// Higher = more willing-but-overlooked: high score, high accept rate, low EPM.
const acceptRateOf = (item) => {
  const inv = item.invitations || {};
  const responded = (inv.accepted || 0) + (inv.declined || 0);
  return responded > 0 ? (inv.accepted || 0) / responded : null;
};
const underutilizedScore = (item) => {
  if (!item.evaluation_count) return -Infinity;
  if (item.average_score < 3.5) return -Infinity;
  const ar = acceptRateOf(item);
  // Treat "no responses yet" as neutral 0.5 so they're not penalized.
  const acceptFactor = ar == null ? 0.5 : ar;
  // High score × high accept ÷ (epm + 0.1) so low EPM bubbles up
  return (item.average_score * acceptFactor) / Math.max(item.events_per_month, 0.1);
};

function WorksRankingSectionComponent({ title, data, avgEpm, language, t, getWorkStatusColor, getWorkStatusLabel }) {
  const [sortMode, setSortMode] = useState('epm_desc');

  const sortedData = useMemo(() => {
    if (!data) return [];
    const arr = [...data];
    switch (sortMode) {
      case 'underutilized':
        arr.sort((a, b) => underutilizedScore(b) - underutilizedScore(a));
        break;
      case 'epm_asc':
        arr.sort((a, b) => a.events_per_month - b.events_per_month);
        break;
      case 'score_desc':
        arr.sort((a, b) => (b.evaluation_count ? b.average_score : -1) - (a.evaluation_count ? a.average_score : -1));
        break;
      case 'accept_desc':
        arr.sort((a, b) => {
          const ra = acceptRateOf(a);
          const rb = acceptRateOf(b);
          // null (no responses) goes to the end
          if (ra == null && rb == null) return 0;
          if (ra == null) return 1;
          if (rb == null) return -1;
          return rb - ra;
        });
        break;
      case 'epm_desc':
      default:
        arr.sort((a, b) => b.events_per_month - a.events_per_month);
    }
    return arr;
  }, [data, sortMode]);

  const sortLabels = {
    epm_desc: language === 'el' ? 'Εργασίες (φθίνουσα)' : 'Works (high → low)',
    epm_asc: language === 'el' ? 'Εργασίες (αύξουσα)' : 'Works (low → high)',
    score_desc: language === 'el' ? 'Βαθμολογία' : 'Score',
    accept_desc: language === 'el' ? '% Αποδοχής' : 'Acceptance %',
    underutilized: language === 'el' ? '⚡ Αξιοποίηση Ταλέντου' : '⚡ Underutilized first'
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="font-heading text-lg truncate">{title}</CardTitle>
            <CardDescription>
              {language === 'el' ? 'Μ.Ο. ομάδας' : 'Group avg'}: <span className="font-semibold text-[#2A1514]">{avgEpm}</span> events/{language === 'el' ? 'μήνα' : 'month'}
            </CardDescription>
          </div>
          <Select value={sortMode} onValueChange={setSortMode}>
            <SelectTrigger className="h-8 w-[180px] text-xs shrink-0" data-testid={`works-sort-${title.replace(/[^a-z]/gi,'').toLowerCase()}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="underutilized">{sortLabels.underutilized}</SelectItem>
              <SelectItem value="epm_desc">{sortLabels.epm_desc}</SelectItem>
              <SelectItem value="epm_asc">{sortLabels.epm_asc}</SelectItem>
              <SelectItem value="score_desc">{sortLabels.score_desc}</SelectItem>
              <SelectItem value="accept_desc">{sortLabels.accept_desc}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400 flex-wrap">
          <span className="flex items-center gap-1"><CheckCircle size={10} className="text-green-500" />{language === 'el' ? 'Αποδοχές' : 'Accepted'}</span>
          <span className="flex items-center gap-1"><XCircle size={10} className="text-red-400" />{language === 'el' ? 'Απορρίψεις' : 'Declined'}</span>
          <span className="flex items-center gap-1"><Clock size={10} className="text-gray-400" />{language === 'el' ? 'Εκκρεμούν' : 'Pending'}</span>
          <span className="text-gray-300">·</span>
          <span className="text-gray-500">
            {language === 'el' ? '% αποδοχής = αποδοχές/(αποδοχές+απορρίψεις)' : '% acceptance = accepted/(accepted+declined)'}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {sortedData && sortedData.length > 0 ? (
          <div className="space-y-2">
            {sortedData.map((item, idx) => (
              <div
                key={item.user_id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors hover:shadow-sm ${
                  item.work_status === 'red' ? 'bg-red-50/60 border-red-100' :
                  item.work_status === 'orange' ? 'bg-orange-50/60 border-orange-100' :
                  'bg-gray-50 border-gray-100'
                }`}
                data-testid={`works-ranking-item-${idx}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                  idx === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2A1514] truncate text-sm">{item.name}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs text-gray-500">
                      {item.total_events} events
                    </span>
                    <span className="text-xs text-gray-300">|</span>
                    <span className={`text-xs font-medium ${
                      item.average_score >= 4 ? 'text-green-600' :
                      item.average_score >= 2.5 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {item.evaluation_count > 0 ? `${item.average_score.toFixed(1)}/5` : '-'}
                    </span>
                    {item.invitations && item.invitations.total > 0 && (
                      <>
                        <span className="text-xs text-gray-300">|</span>
                        <span className="text-xs text-green-600" title={language === 'el' ? 'Αποδοχές' : 'Accepted'}>
                          {item.invitations.accepted}
                        </span>
                        <span className="text-xs text-red-500" title={language === 'el' ? 'Απορρίψεις' : 'Declined'}>
                          {item.invitations.declined}
                        </span>
                        <span className="text-xs text-gray-400" title={language === 'el' ? 'Εκκρεμούν' : 'Pending'}>
                          {item.invitations.pending}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-1">
                  <span className={`text-lg font-bold ${
                    item.work_status === 'red' ? 'text-red-600' :
                    item.work_status === 'orange' ? 'text-orange-500' :
                    'text-green-600'
                  }`}>
                    {item.events_per_month}
                  </span>
                  {(() => {
                    const inv = item.invitations || {};
                    const responded = (inv.accepted || 0) + (inv.declined || 0);
                    if (responded === 0) {
                      return (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 bg-gray-50 border-gray-200 text-gray-500"
                          title={language === 'el' ? 'Καμία απάντηση ακόμα' : 'No responses yet'}
                          data-testid={`accept-rate-${idx}`}
                        >
                          —
                        </Badge>
                      );
                    }
                    const rate = Math.round(((inv.accepted || 0) / responded) * 100);
                    const cls = rate >= 70
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : rate >= 40
                      ? 'bg-amber-50 border-amber-200 text-amber-700'
                      : 'bg-red-50 border-red-200 text-red-700';
                    return (
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 ${cls}`}
                        title={language === 'el'
                          ? `Ποσοστό αποδοχής: ${inv.accepted}/${responded} προσκλήσεις (αγνοούνται οι εκκρεμείς)`
                          : `Acceptance rate: ${inv.accepted}/${responded} responded invites (pending excluded)`}
                        data-testid={`accept-rate-${idx}`}
                      >
                        {rate}% {language === 'el' ? 'αποδ.' : 'accept'}
                      </Badge>
                    );
                  })()}
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getWorkStatusColor(item.work_status)}`}>
                    {getWorkStatusLabel(item.work_status)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Briefcase size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">{t('noData')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user, isAdmin, isSeniorFacilitator } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [period, setPeriod] = useState('all_time');
  const [stats, setStats] = useState(null);
  const [recentEvaluations, setRecentEvaluations] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [worksData, setWorksData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [period, user]);

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const promises = [
        statsApi.getUserStats(user.user_id, period),
        awardsApi.getLeaderboard(period),
        statsApi.getWorksPerMonth(period)
      ];
      if (!isAdmin) {
        promises.push(evaluationsApi.getMy({ period }));
      }

      const results = await Promise.all(promises);
      
      setStats(results[0].data);
      setLeaderboard((results[1].data.facilitators || []).slice(0, 5));
      setWorksData(results[2].data);

      if (!isAdmin) {
        setRecentEvaluations(results[3].data.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadMonthlyReport = () => {
    const now = new Date();
    exportApi.monthlyReport(now.getMonth() + 1, now.getFullYear());
  };

  const handleDownloadWorksReport = async () => {
    try {
      await exportApi.worksPerMonthPdf(period);
      toast.success(language === 'el' ? 'PDF κατέβηκε!' : 'PDF downloaded!');
    } catch {
      toast.error(language === 'el' ? 'Σφάλμα λήψης PDF' : 'Error downloading PDF');
    }
  };

  const handleDownloadWorksExcel = async () => {
    try {
      await exportApi.worksPerMonthExcel(period);
      toast.success(language === 'el' ? 'Excel κατέβηκε!' : 'Excel downloaded!');
    } catch {
      toast.error(language === 'el' ? 'Σφάλμα λήψης Excel' : 'Error downloading Excel');
    }
  };

  const [syncing, setSyncing] = useState(false);
  const handleSyncInvitations = async () => {
    setSyncing(true);
    try {
      const res = await eventsApi.syncAllInvitations();
      toast.success(res.data.message || (language === 'el' ? 'Ενημερώθηκαν!' : 'Synced!'));
      loadData();
    } catch {
      toast.error(language === 'el' ? 'Σφάλμα συγχρονισμού' : 'Sync error');
    } finally {
      setSyncing(false);
    }
  };

  const timePeriods = [
    { value: 'this_month', label: t('thisMonth') },
    { value: 'quarter', label: t('quarter') },
    { value: 'semester', label: t('semester') },
    { value: 'year', label: t('year') },
    { value: 'two_years', label: t('twoYears') },
    { value: 'all_time', label: t('allTime') },
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getWorkStatusColor = (status) => {
    if (status === 'red') return 'bg-red-100 text-red-700 border-red-200';
    if (status === 'orange') return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const getWorkStatusLabel = (status) => {
    if (status === 'red') return language === 'el' ? 'Χαμηλή' : 'Low';
    if (status === 'orange') return language === 'el' ? 'Κάτω μ.ο.' : 'Below avg';
    return language === 'el' ? 'Κανονική' : 'Normal';
  };

  const WorksRankingSection = ({ title, data, avgEpm }) => (
    <WorksRankingSectionComponent
      title={title}
      data={data}
      avgEpm={avgEpm}
      language={language}
      t={t}
      getWorkStatusColor={getWorkStatusColor}
      getWorkStatusLabel={getWorkStatusLabel}
    />
  );

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in-up" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#2A1514]">
              {language === 'el' ? 'Καλώς ήρθες' : 'Welcome'}, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              {isAdmin
                ? (language === 'el' ? 'Παρακολούθηση εργασιών και απόδοσης ομάδας' : 'Track team workload and performance')
                : (language === 'el' ? 'Παρακολουθησε την προοδο σου και τις αξιολογησεις σου' : 'Track your progress and evaluations')
              }
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isAdmin && (
              <Button 
                variant="outline"
                onClick={handleDownloadMonthlyReport}
                className="border-[#E6007E] text-[#E6007E] hover:bg-[#E6007E]/5 text-sm"
                data-testid="download-report-btn"
              >
                <FileText size={16} className="mr-1.5" />
                <span className="hidden sm:inline">{language === 'el' ? 'Μηνιαια Αναφορα' : 'Monthly Report'}</span>
                <span className="sm:hidden">{language === 'el' ? 'Αναφορα' : 'Report'}</span>
              </Button>
            )}
            {isAdmin && (
              <Button 
                variant="outline"
                onClick={handleSyncInvitations}
                disabled={syncing}
                className="border-blue-300 text-blue-600 hover:bg-blue-50 text-sm"
                data-testid="sync-invitations-btn"
              >
                <RefreshCw size={16} className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{syncing ? (language === 'el' ? 'Συγχρονισμός...' : 'Syncing...') : (language === 'el' ? 'Συγχρονισμός Προσκλήσεων' : 'Sync Invitations')}</span>
                <span className="sm:hidden">Sync</span>
              </Button>
            )}
            {isAdmin && (
              <Button 
                variant="outline"
                onClick={handleDownloadWorksReport}
                className="border-[#E6007E] text-[#E6007E] hover:bg-[#E6007E]/5 text-sm"
                data-testid="download-works-report-btn"
              >
                <Download size={16} className="mr-1.5" />
                <span className="hidden sm:inline">{language === 'el' ? 'Αναφορά PDF' : 'PDF Report'}</span>
                <span className="sm:hidden">PDF</span>
              </Button>
            )}
            {isAdmin && (
              <Button 
                variant="outline"
                onClick={handleDownloadWorksExcel}
                className="border-green-600 text-green-600 hover:bg-green-50 text-sm"
                data-testid="download-works-excel-btn"
              >
                <FileSpreadsheet size={16} className="mr-1.5" />
                <span className="hidden sm:inline">Excel</span>
                <span className="sm:hidden">XLS</span>
              </Button>
            )}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px] sm:w-[160px]" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timePeriods.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isAdmin && (
              <Button 
                onClick={() => navigate('/evaluations/new')}
                className="bg-[#E6007E] hover:bg-[#b8005f] text-sm"
                data-testid="new-evaluation-btn"
              >
                <Plus size={16} className="mr-1.5" />
                <span className="hidden sm:inline">{t('newEvaluation')}</span>
                <span className="sm:hidden">{language === 'el' ? 'Νεα' : 'New'}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Admin Dashboard: Works/Month Ranking */}
        {isAdmin && worksData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WorksRankingSection
              title={language === 'el' ? 'Εργασίες/Μήνα — Facilitators' : 'Works/Month — Facilitators'}
              data={worksData.facilitators}
              avgEpm={worksData.facilitator_avg_epm}
            />
            <WorksRankingSection
              title={language === 'el' ? 'Εργασίες/Μήνα — Senior Facilitators' : 'Works/Month — Senior Facilitators'}
              data={worksData.seniors}
              avgEpm={worksData.senior_avg_epm}
            />
          </div>
        )}

        {/* Non-Admin: Stats Grid */}
        {!isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {/* Score Card */}
            <Card className="border-0 shadow-lg col-span-1 md:col-span-2 lg:col-span-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#E6007E]/10 rounded-xl flex items-center justify-center">
                    <Star className="text-[#E6007E]" size={24} />
                  </div>
                  <StatusBadge status={stats?.status || 'green'} size="small" animated />
                </div>
                <p className="text-sm text-gray-500 mb-1">{t('yourScore')}</p>
                <div className="flex items-end gap-2">
                  <ScoreDisplay score={stats?.average_score || 0} />
                  <span className="text-gray-400 text-lg mb-1">/5</span>
                </div>
              </CardContent>
            </Card>

            {/* Evaluations Count */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <ClipboardCheck className="text-blue-600" size={24} />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-1">{t('totalEvaluations')}</p>
                <p className="text-3xl font-bold text-[#2A1514]">{stats?.evaluation_count || 0}</p>
              </CardContent>
            </Card>

            {/* Trend Card */}
            <Card className="border-0 shadow-lg col-span-1 md:col-span-2 lg:col-span-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-500">{language === 'el' ? 'Τάση Βαθμολογίας' : 'Score Trend'}</p>
                  </div>
                  <TrendingUp className="text-green-500" size={20} />
                </div>
                {stats?.trend && stats.trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={80}>
                    <LineChart data={stats.trend}>
                      <XAxis dataKey="date" hide />
                      <YAxis domain={[1, 5]} hide />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                        formatter={(value) => [value.toFixed(1), 'Score']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#E6007E" 
                        strokeWidth={2}
                        dot={{ fill: '#E6007E', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-400 text-sm text-center py-4">{t('noData')}</p>
                )}
              </CardContent>
            </Card>

            {/* Works/Month Personal Card */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                {(() => {
                  const myRole = user?.role;
                  const list = myRole === 'senior_facilitator' ? worksData?.seniors : worksData?.facilitators;
                  const avgEpm = myRole === 'senior_facilitator' ? worksData?.senior_avg_epm : worksData?.facilitator_avg_epm;
                  const me = list?.find(e => e.user_id === user?.user_id);
                  const rank = list ? list.findIndex(e => e.user_id === user?.user_id) + 1 : 0;
                  const statusColor = me?.work_status === 'green' ? 'text-green-600 bg-green-100' : me?.work_status === 'orange' ? 'text-orange-600 bg-orange-100' : 'text-red-600 bg-red-100';
                  return (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <Briefcase className="text-purple-600" size={24} />
                        </div>
                        {me && <span className={`text-xs font-bold px-2 py-1 rounded-full ${statusColor}`}>{rank > 0 ? `#${rank}` : '-'}</span>}
                      </div>
                      <p className="text-sm text-gray-500 mb-1">{language === 'el' ? 'Εργασίες/Μήνα' : 'Works/Month'}</p>
                      <p className="text-3xl font-bold text-[#2A1514]">{me?.events_per_month ?? '-'}</p>
                      {avgEpm != null && <p className="text-[10px] text-gray-400 mt-1">{language === 'el' ? 'Μ.Ο. ομάδας' : 'Group avg'}: {avgEpm}</p>}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admin: Underutilized Talent Alert */}
        {isAdmin && worksData?.underutilized?.length > 0 && (
          <Card className="border-0 shadow-lg border-l-4 border-l-amber-400 bg-gradient-to-r from-amber-50/50 to-white">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Star className="text-amber-600" size={16} />
                </div>
                <div>
                  <CardTitle className="text-sm font-semibold text-[#2A1514]">
                    {language === 'el' ? 'Αξιοποίηση Ταλέντου' : 'Talent Utilization'}
                  </CardTitle>
                  <CardDescription className="text-[10px]">
                    {language === 'el'
                      ? 'Facilitators με υψηλή βαθμολογία & θετική ανταπόκριση αλλά λίγες εργασίες'
                      : 'High-scoring facilitators with positive response but low workload'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-2">
                {worksData.underutilized.map(u => (
                  <div key={u.user_id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-amber-200 shadow-sm">
                    <span className="text-xs font-medium text-[#2A1514]">{u.name}</span>
                    <span className="text-[10px] text-amber-700 font-semibold">{u.average_score.toFixed(1)}</span>
                    <span className="text-[10px] text-gray-400">{u.events_per_month} {language === 'el' ? 'εργ/μ' : 'w/m'}</span>
                    {u.acceptance_rate > 0 && <span className="text-[10px] text-green-600">{u.acceptance_rate}%</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Recent Evaluations - Non-admin only */}
          {!isAdmin && (
            <Card className="border-0 shadow-lg lg:col-span-8">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg">{t('recentEvaluations')}</CardTitle>
                    <CardDescription>{language === 'el' ? 'Τελευταίες αξιολογήσεις σας' : 'Your latest evaluations'}</CardDescription>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/evaluations')}
                    className="text-[#E6007E]"
                  >
                    {t('view')} <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {recentEvaluations.length > 0 ? (
                  <div className="space-y-3">
                    {recentEvaluations.map((eval_item, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <ClipboardCheck size={20} className="text-gray-400" />
                          </div>
                          <div>
                            <p className="font-medium text-[#2A1514]">
                              {eval_item.evaluation_type === 'self' ? t('selfEvaluation') : 
                               eval_item.evaluation_type === 'peer' ? t('peerEvaluation') : 
                               t('supervisorEvaluation')}
                            </p>
                            <p className="text-sm text-gray-500">{formatDate(eval_item.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <ScoreBar score={eval_item.average_score} showValue={false} />
                          <StatusBadge status={eval_item.status} size="small" showLabel={false} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ClipboardCheck size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">{t('noEvaluations')}</p>
                    <Button 
                      onClick={() => navigate('/evaluations/new')}
                      className="mt-4 bg-[#E6007E] hover:bg-[#b8005f]"
                    >
                      {t('newEvaluation')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Leaderboard / Top Performers */}
          <Card className={`border-0 shadow-lg ${isAdmin ? 'lg:col-span-12' : 'lg:col-span-4'}`}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">{t('topPerformers')}</CardTitle>
                  <CardDescription>{t('leaderboard')}</CardDescription>
                </div>
                <Trophy className="text-yellow-500" size={20} />
              </div>
            </CardHeader>
            <CardContent>
              {leaderboard.length > 0 ? (
                <div className={`${isAdmin ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' : 'space-y-3'}`}>
                  {leaderboard.map((item, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                        ${idx === 0 ? 'bg-yellow-100 text-yellow-600' : 
                          idx === 1 ? 'bg-gray-100 text-gray-600' :
                          idx === 2 ? 'bg-orange-100 text-orange-600' :
                          'bg-gray-50 text-gray-400'}
                      `}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#2A1514] truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {item.evaluation_count} {language === 'el' ? 'αξιολογήσεις' : 'evaluations'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          item.average_score >= 4 ? 'text-green-600' :
                          item.average_score >= 3 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {item.average_score.toFixed(1)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Trophy size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">{t('noData')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
